import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import RFMHistory from '../components/RFMHistory';
import RFMMap from '../components/RFMMap';
import { savedSegmentsAPI, analyticsAPI, clientsAPI } from '../services/api';
import { Search, ArrowLeft, Settings, Edit2, Trash2 } from 'lucide-react';
import Pagination from '../components/Pagination';
import toast from 'react-hot-toast';

const PERIODS = [
  { value: 'week', label: 'Неделя' },
  { value: 'month', label: 'Последний месяц' },
  { value: 'quarter', label: 'Квартал' },
  { value: 'year', label: 'Год' },
  { value: 'all', label: 'Всё время' },
];
const PER_PAGE = 50;
function formatMoney(v) { return v != null ? Math.round(v).toLocaleString('ru-RU') + ' ₽' : '0 ₽'; }

const RFM_COLORS = {
  'Новые': '#06b6d4', 'Потерянные': '#6b7280', 'Засыпающие': '#fb923c',
  'Перспективные': '#84cc16', 'VIP': '#a78bfa', 'Активные': '#10b981',
  'Требуют внимания': '#f59e0b', 'Спящие': '#ef4444', 'Лояльные': '#6366f1',
};
const getRfmColor = (name) => RFM_COLORS[name] || '#6b7280';

function SegmentClientsView({ segment, onBack }) {
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    clientsAPI.segmentsBatch([]).then(() => {}).catch(() => {});
    clientsAPI.filtered({ skip: (page - 1) * PER_PAGE, limit: PER_PAGE, segment_filter: segment.filters })
      .then(r => { setClients(r.data.items || []); setTotal(r.data.total || 0); }).catch(() => {}).finally(() => setLoading(false));
  }, [page, segment.id]);
  return (
    <div>
      <button className="btn btn-ghost btn-sm" onClick={onBack} style={{ marginBottom: 12 }}><ArrowLeft size={14} /> Назад</button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: segment.color || '#6366f1' }} />
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{segment.name}</h2>
        <span style={{ color: '#9ca3af', fontSize: 13 }}>{total} клиентов</span>
      </div>
      {loading ? <div style={{ padding: 20, color: '#9ca3af' }}>Загрузка...</div> : (<>
        <div className="table-container"><table className="table" style={{ fontSize: 12 }}>
          <thead><tr><th>Телефон</th><th>Имя</th><th>Email</th><th>Уровень</th><th>Баланс</th><th>Регистрация</th></tr></thead>
          <tbody>
            {clients.map(c => (<tr key={c.id}>
              <td style={{ fontFamily: 'var(--font-mono)' }}>{c.phone}</td>
              <td style={{ fontWeight: 500 }}>{c.name || '—'}</td>
              <td style={{ color: '#6b7280' }}>{c.email || '—'}</td>
              <td><span className="badge badge-info">{c.loyalty_tier || '—'}</span></td>
              <td style={{ fontFamily: 'var(--font-mono)' }}>{Math.round(c.balance || 0)}</td>
              <td style={{ color: '#9ca3af' }}>{c.created_at ? new Date(c.created_at).toLocaleDateString('ru') : '—'}</td>
            </tr>))}
            {clients.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>Нет клиентов</td></tr>}
          </tbody>
        </table></div>
        <Pagination page={page} totalPages={Math.ceil(total / PER_PAGE)} onPageChange={setPage} />
      </>)}
    </div>
  );
}

function EditSegmentModal({ segment, onClose, onSaved }) {
  const [name, setName] = useState(segment.name);
  const [color, setColor] = useState(segment.color || '#6366f1');
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Введите название'); return; }
    setLoading(true);
    try {
      await savedSegmentsAPI.update(segment.id, { name: name.trim(), color });
      toast.success('Сегмент обновлён');
      onSaved(); onClose();
    } catch { toast.error('Ошибка'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
        <h2 className="modal-title">Редактировать сегмент</h2>
        <div className="form-group"><label className="form-label">Название</label>
          <input className="form-input" value={name} onChange={e => setName(e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Цвет</label>
          <input type="color" value={color} onChange={e => setColor(e.target.value)}
            style={{ width: 40, height: 32, border: 'none', padding: 0, cursor: 'pointer', borderRadius: 4 }} /></div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>{loading ? '...' : 'Сохранить'}</button>
        </div>
      </div>
    </div>
  );
}

function RFMView() {
  const [rfm, setRfm] = useState(null);
  const [rfmError, setRfmError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    analyticsAPI.rfmStats().then(r => { setRfm(r.data); setRfmError(''); }).catch(() => setRfmError('Не удалось загрузить RFM. Обновите страницу.')).finally(() => setLoading(false));
    analyticsAPI.rfmConfig().then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await analyticsAPI.updateRfmConfig(config);
      toast.success('Настройки сохранены');
      setShowConfig(false);
      setLoading(true);
      analyticsAPI.rfmStats().then(r => { setRfm(r.data); setRfmError(''); }).catch(() => setRfmError('Не удалось загрузить RFM. Обновите страницу.')).finally(() => setLoading(false));
    } catch { toast.error('Ошибка'); }
    finally { setSaving(false); }
  };

  if (selected) return <SegmentClientsView segment={{ id: selected.name, name: selected.name, color: getRfmColor(selected.name), filters: { segments: [selected.name] } }} onBack={() => setSelected(null)} />;
  if (loading) return <div style={{ padding: 20, color: '#9ca3af' }}>Загрузка RFM...</div>;
  if (rfmError) return <p role="alert">{rfmError}</p>;
  if (!rfm?.segments) return <div style={{ padding: 20, color: '#9ca3af' }}>Нет данных</div>;


  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setShowConfig(!showConfig)}>
          <Settings size={14} /> Настройки порогов
        </button>
      </div>

      {showConfig && config && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 16, background: 'var(--bg-secondary)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>Режим скоринга</span>
            <select className="form-input" style={{ width: 200 }} value={config.scoring_mode}
              onChange={e => setConfig({ ...config, scoring_mode: e.target.value })}>
              <option value="auto">Автоматический (квинтили)</option>
              <option value="manual">Ручные пороги</option>
            </select>
          </div>
          {config.scoring_mode === 'manual' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8, color: '#6366f1' }}>Recency (дней с покупки)</div>
                {[['r5_max','R5 — до (дней)'],['r4_max','R4 — до'],['r3_max','R3 — до'],['r2_max','R2 — до']].map(([k,l]) => (
                  <div className="form-group" key={k}><label className="form-label">{l}</label>
                    <input className="form-input" type="number" value={config[k]} onChange={e => setConfig({ ...config, [k]: parseInt(e.target.value) || 0 })} /></div>
                ))}
                <div style={{ fontSize: 10, color: '#9ca3af' }}>R1 = больше {config.r2_max} дней</div>
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8, color: '#22c55e' }}>Frequency (заказов)</div>
                {[['f2_min','F2 — от'],['f3_min','F3 — от'],['f4_min','F4 — от'],['f5_min','F5 — от']].map(([k,l]) => (
                  <div className="form-group" key={k}><label className="form-label">{l}</label>
                    <input className="form-input" type="number" value={config[k]} onChange={e => setConfig({ ...config, [k]: parseInt(e.target.value) || 0 })} /></div>
                ))}
                <div style={{ fontSize: 10, color: '#9ca3af' }}>F1 = меньше {config.f2_min}</div>
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8, color: '#f59e0b' }}>Monetary (сумма, ₽)</div>
                {[['m2_min','M2 — от'],['m3_min','M3 — от'],['m4_min','M4 — от'],['m5_min','M5 — от']].map(([k,l]) => (
                  <div className="form-group" key={k}><label className="form-label">{l}</label>
                    <input className="form-input" type="number" value={config[k]} onChange={e => setConfig({ ...config, [k]: parseFloat(e.target.value) || 0 })} /></div>
                ))}
                <div style={{ fontSize: 10, color: '#9ca3af' }}>M1 = меньше {config.m2_min} ₽</div>
              </div>
            </div>
          )}
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={handleSaveConfig} disabled={saving}>{saving ? '...' : 'Сохранить'}</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowConfig(false)}>Отмена</button>
          </div>
        </div>
      )}

      <RFMMap stats={rfm} config={config} onSelect={setSelected} />
    </div>
  );
}

export default function SegmentsPage() {
  const [params,setParams]=useSearchParams();
  const tab=['rfm','rfm-history'].includes(params.get('tab'))?params.get('tab'):'simple';
  const setTab=value=>setParams(value==='simple'?{}:{tab:value});
  const [period, setPeriod] = useState('month');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editSeg, setEditSeg] = useState(null);

  const loadStats = () => {
    setLoading(true);
    savedSegmentsAPI.stats(period).then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { if(tab==='simple')loadStats(); }, [period,tab]);

  const filtered = (stats?.segments || []).filter(s => !search || s.name.toLowerCase().includes(search.toLowerCase()));

  const handleDelete = async (seg) => {
    if (!window.confirm(`Удалить сегмент «${seg.name}»?`)) return;
    try { await savedSegmentsAPI.delete(seg.id); toast.success('Удалён'); loadStats(); } catch { toast.error('Ошибка'); }
  };

  if (selected) return (
    <div className="animate-in">
      <SegmentClientsView segment={selected} onBack={() => setSelected(null)} />
    </div>
  );

  return (
    <div className="animate-in">
      <div className="page-header"><h1 className="page-title">Сегменты</h1></div>
      <div style={{ display: tab!=='simple'?'none':'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 400 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: '#9ca3af' }} />
          <input className="form-input" style={{ paddingLeft: 32 }} placeholder="Название сегмента" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#6b7280' }}>Показать расчёты за</span>
          <select className="form-input" style={{ width: 180 }} value={period} onChange={e => setPeriod(e.target.value)}>
            {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid var(--border)' }}>
        {[{ key: 'simple', label: 'Простые', count: stats?.segments?.length }, { key: 'rfm', label: 'RFM' }, { key: 'rfm-history', label: 'Динамика RFM' }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
            background: 'transparent', borderBottom: tab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
            color: tab === t.key ? 'var(--primary)' : 'var(--text-muted)', marginBottom: -2,
          }}>{t.label} {t.count != null && <span className="badge" style={{ marginLeft: 6, fontSize: 10 }}>{t.count}</span>}</button>
        ))}
      </div>

      {tab === 'simple' && (loading ? <div style={{ padding: 20, color: '#9ca3af' }}>Загрузка...</div> : (
        <div className="table-container"><table className="table" style={{ fontSize: 13 }}>
          <thead><tr>
            <th>Сегмент</th><th>Клиентов</th><th>CR</th>
            <th>Средний чек</th><th>Выручка</th><th>Расходы</th><th>Ст-ть покупателя</th><th></th>
          </tr></thead>
          <tbody>
            {filtered.map(s => (
              <tr key={s.id}>
                <td style={{ cursor: 'pointer' }} onClick={() => setSelected({ id: s.id, name: s.name, color: s.color, filters: s.filters || {} })}>
                  <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: s.color || '#6366f1', color: '#fff' }}>{s.name}</span>
                </td>
                <td style={{ fontFamily: 'var(--font-mono)' }}>{(s.client_count || 0).toLocaleString('ru-RU')}</td>
                <td style={{ fontFamily: 'var(--font-mono)' }}>{s.cr || 0}%</td>
                <td style={{ fontFamily: 'var(--font-mono)' }}>{formatMoney(s.avg_check)}</td>
                <td style={{ fontFamily: 'var(--font-mono)', color: s.revenue > 0 ? '#22c55e' : undefined }}>{formatMoney(s.revenue)}</td>
                <td style={{ fontFamily: 'var(--font-mono)', color: s.expenses > 0 ? '#ef4444' : undefined }}>{formatMoney(s.expenses)}</td>
                <td style={{ fontFamily: 'var(--font-mono)' }}>{formatMoney(s.cac)}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditSeg(s)}><Edit2 size={13} /></button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(s)}><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>
              {search ? 'Ничего не найдено' : 'Нет сохранённых сегментов'}
            </td></tr>}
          </tbody>
          {stats?.total && filtered.length > 0 && (
            <tfoot><tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
              <td>Все клиенты</td>
              <td style={{ fontFamily: 'var(--font-mono)' }}>{(stats.total.client_count || 0).toLocaleString('ru-RU')}</td>
              <td style={{ fontFamily: 'var(--font-mono)' }}>{stats.total.cr || 0}%</td>
              <td style={{ fontFamily: 'var(--font-mono)' }}>{formatMoney(stats.total.avg_check)}</td>
              <td style={{ fontFamily: 'var(--font-mono)', color: '#22c55e' }}>{formatMoney(stats.total.revenue)}</td>
              <td style={{ fontFamily: 'var(--font-mono)', color: '#ef4444' }}>{formatMoney(stats.total.expenses)}</td>
              <td style={{ fontFamily: 'var(--font-mono)' }}>{formatMoney(stats.total.cac)}</td>
              <td></td>
            </tr></tfoot>
          )}
        </table></div>
      ))}

      {tab === 'rfm' && <RFMView />}
      {tab === 'rfm-history' && <RFMHistory />}

      {editSeg && <EditSegmentModal segment={editSeg} onClose={() => setEditSeg(null)} onSaved={loadStats} />}
    </div>
  );
}